import { useQuery } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router';

import type { StorefrontCategory } from '@/features/storefront/api/storefront';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import type { FooterProps } from '@/features/storefront/layouts/chrome/types';
import { useStorefrontPaths } from '@/features/storefront/paths';
import {
  LinkDirectoryView,
  linkDirectoryGroups,
} from '@/features/storefront/sections/LinkDirectorySection';
import { linkDirectorySectionSchema } from '@/features/storefront/sections/schema';
import { STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { appStoreHref } from '@/features/storefront/themeSchema';
import { cn } from '@/lib/cn';

/**
 * What the footer carries beyond its own layout: the dense link directory a
 * marketplace keeps above its footer, and the merchant's app-store badges.
 *
 * Both are drawn from `ThemeFooter` alone, so every footer - the family's own,
 * the columns one, the large one - wears them the same way.
 */

type Footer = FooterProps['footer'];

/**
 * The footer's directory as a `link_directory` section: the same groups and
 * the same drawing as the section, in the footer's fixed dense look.
 */
function footerDirectorySection(footer: Footer) {
  const source = footer?.directory ?? 'off';
  if (source === 'off') return null;
  return linkDirectorySectionSchema.parse({
    type: 'link_directory',
    title: footer?.directory_title ?? '',
    source,
    separator: 'pipe',
    columns: 3,
    heading_style: 'bold',
    text_size: 'xs',
    blocks: footer?.directory_blocks ?? [],
  });
}

/**
 * The grouped links above the footer, in the footer's own colours - it is
 * drawn inside the wrapper that carries them. Nothing while it is off, or
 * while there is nothing to list.
 *
 * `categories` is the caller's own list (the customiser has the merchant's);
 * without one the shop is asked, the same query the header strip shares.
 */
export function FooterDirectory({
  footer,
  preview,
  categories: given,
}: {
  footer: Footer;
  preview?: boolean;
  categories?: StorefrontCategory[];
}) {
  const section = useMemo(() => footerDirectorySection(footer), [footer]);
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const paths = useStorefrontPaths();
  const query = useQuery({
    queryKey: [...scope, 'categories'],
    queryFn: () => source.categories(),
    enabled: !given && !preview && section?.source === 'categories',
  });
  if (!section) return null;
  const groups = linkDirectoryGroups(section, given ?? query.data ?? []);
  if (groups.length === 0) return null;

  const title = section.title.trim();
  return (
    <div
      data-footer-directory=""
      className="border-t py-8"
      style={{ borderColor: 'var(--storefront-surface)', color: 'var(--storefront-text)' }}
    >
      <LinkDirectoryView
        section={section}
        groups={groups}
        heading={
          title ? (
            <h2 className="mb-4 text-base" style={STOREFRONT_HEADING_STYLE}>
              {title}
            </h2>
          ) : undefined
        }
        renderLink={(href, className, children, style) =>
          preview ? (
            <span className={className} style={style}>
              {children}
            </span>
          ) : (
            <Link to={paths.menu(href)} className={className} style={style}>
              {children}
            </Link>
          )
        }
      />
    </div>
  );
}

/** The Google Play mark: a plain four-colour triangle, drawn here. */
function PlayMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-6 shrink-0" aria-hidden="true" focusable="false">
      <path d="M4 2.5 13.6 12 4 21.5c-.3-.2-.5-.6-.5-1v-17c0-.4.2-.8.5-1Z" fill="#34A853" />
      <path d="M16.8 8.8 13.6 12 4 2.5c.3-.2.8-.2 1.2 0l11.6 6.3Z" fill="#4285F4" />
      <path d="M16.8 15.2 5.2 21.5c-.4.2-.9.2-1.2 0l9.6-9.5 3.2 3.2Z" fill="#EA4335" />
      <path d="m16.8 8.8 3.4 1.9c1 .6 1 2 0 2.6l-3.4 1.9-3.2-3.2 3.2-3.2Z" fill="#FBBC04" />
    </svg>
  );
}

/** A simple apple, drawn here rather than shipped as a badge file. */
function AppleMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-6 shrink-0" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M16.4 12.6c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9-.7 0-1.8-.9-3-.8-1.5 0-3 .9-3.8 2.3-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.4 2.9 2.3 1.2 0 1.6-.7 3-.7s1.8.7 3 .7c1.3 0 2.1-1.1 2.8-2.3.9-1.3 1.3-2.6 1.3-2.6s-2.5-1-2.5-3.8ZM14.1 5.8c.6-.8 1.1-1.8 1-2.8-.9 0-2 .6-2.7 1.4-.6.7-1.1 1.7-1 2.7 1 .1 2-.5 2.7-1.3Z"
      />
    </svg>
  );
}

function Badge({
  href,
  preview,
  label,
  small,
  big,
  mark,
}: {
  href: string;
  preview?: boolean;
  label: string;
  small: string;
  big: string;
  mark: ReactNode;
}) {
  const className =
    'inline-flex h-11 min-w-[9.5rem] items-center gap-2 rounded-lg border px-3 text-start transition-opacity hover:opacity-80';
  // A store badge is black wherever it stands; the hairline keeps it from
  // vanishing into a dark footer.
  const style = { backgroundColor: '#000', color: '#fff', borderColor: 'rgba(255,255,255,0.35)' };
  const body = (
    <>
      {mark}
      <span className="flex flex-col leading-none">
        <span className="text-[0.625rem] uppercase tracking-wide">{small}</span>
        <span className="text-base font-semibold">{big}</span>
      </span>
    </>
  );
  if (preview) {
    return (
      <span className={className} style={style} aria-label={label} role="img">
        {body}
      </span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className={className}
      style={style}
    >
      {body}
    </a>
  );
}

/**
 * "Get it on Google Play" / "Download on the App Store", for each listing
 * the merchant has added. Nothing at all without one; a link that is not the
 * official store is never drawn.
 */
export function AppStoreBadges({
  footer,
  preview,
  className,
}: {
  footer: Footer;
  preview?: boolean;
  className?: string;
}) {
  const copy = useShopContent();
  const play = appStoreHref('play_store_url', footer?.play_store_url);
  const apple = appStoreHref('app_store_url', footer?.app_store_url);
  if (!play && !apple) return null;
  const getItOn = copy('pwa.get_it_on');
  const downloadOn = copy('pwa.download_on');
  return (
    <div
      role="group"
      aria-label={copy('pwa.app_links')}
      data-footer-app-badges=""
      className={cn('flex flex-wrap gap-2', className)}
    >
      {play ? (
        <Badge
          href={play}
          preview={preview}
          label={`${getItOn} Google Play`}
          small={getItOn}
          big="Google Play"
          mark={<PlayMark />}
        />
      ) : null}
      {apple ? (
        <Badge
          href={apple}
          preview={preview}
          label={`${downloadOn} App Store`}
          small={downloadOn}
          big="App Store"
          mark={<AppleMark />}
        />
      ) : null}
    </div>
  );
}
